import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { Topbar } from '@/components/Topbar';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/StatusPill';
import { useAsync } from '@/hooks/useAsync';
import { haptic } from '@/lib/telegram';
import type {
  Campaign,
  CampaignLaunchPreview,
  CampaignLaunchPreviewItem,
  LaunchMode,
} from '@/types/domain';

type LaunchSettingsState = {
  scheduleStartHour: number;
  scheduleEndHour: number;
  intervalMinutes: number;
  maxChatsPerAccountPerDay: number;
  minDelayBetweenChatsSec: number;
  joinDelayMinSec: number;
  joinDelayMaxSec: number;
  typingMinMs: number;
  typingMaxMs: number;
  seedStepDelayMinSec: number;
  seedStepDelayMaxSec: number;
  chatReplyDelayMinSec: number;
  chatReplyDelayMaxSec: number;
  dmReplyDelayMinSec: number;
  dmReplyDelayMaxSec: number;
  maxChatRepliesPerUser: number;
  maxDmRepliesPerDialog: number;
  seedMessagesMin: number;
  seedMessagesMax: number;
  uniqueDialogPerChat: boolean;
};

const MODE_CARDS: Array<{
  mode: LaunchMode;
  title: string;
  subtitle: string;
  icon: 'users' | 'play' | 'sparkles';
}> = [
  {
    mode: 'join',
    title: 'Вступить в чаты',
    subtitle: 'Проверка уже покрытых чатов, preview задач и создание join-задач.',
    icon: 'users',
  },
  {
    mode: 'seed_all',
    title: 'Посев по всем',
    subtitle: 'Создаёт посевы во всех подходящих чатах, даже если раньше уже был запуск.',
    icon: 'play',
  },
  {
    mode: 'seed_new',
    title: 'Посев по новым',
    subtitle: 'Создаёт посевы только там, где ещё не было размещения.',
    icon: 'sparkles',
  },
];

const DEFAULT_SETTINGS: LaunchSettingsState = {
  scheduleStartHour: 10,
  scheduleEndHour: 22,
  intervalMinutes: 60,
  maxChatsPerAccountPerDay: 8,
  minDelayBetweenChatsSec: 600,
  joinDelayMinSec: 45,
  joinDelayMaxSec: 180,
  typingMinMs: 2500,
  typingMaxMs: 9000,
  seedStepDelayMinSec: 20,
  seedStepDelayMaxSec: 180,
  chatReplyDelayMinSec: 20,
  chatReplyDelayMaxSec: 180,
  dmReplyDelayMinSec: 60,
  dmReplyDelayMaxSec: 300,
  maxChatRepliesPerUser: 1,
  maxDmRepliesPerDialog: 1,
  seedMessagesMin: 2,
  seedMessagesMax: 5,
  uniqueDialogPerChat: true,
};

export function CampaignLaunchPage(): JSX.Element {
  const { id } = useParams();
  const cid = Number(id);
  const { data: campaign, reload } = useAsync(() => api.getCampaign(cid), [cid]);
  const nav = useNavigate();

  const [settings, setSettings] = useState<LaunchSettingsState>(DEFAULT_SETTINGS);
  const [preview, setPreview] = useState<CampaignLaunchPreview | null>(null);
  const [selectedMode, setSelectedMode] = useState<LaunchMode | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState<LaunchMode | null>(null);
  const [executeBusy, setExecuteBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!campaign) return;
    setSettings(toSettings(campaign));
  }, [campaign]);

  const patchSettings = <K extends keyof LaunchSettingsState>(key: K, value: LaunchSettingsState[K]) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const persistSettings = async (opts?: { silent?: boolean; reloadAfter?: boolean }) => {
    const silent = Boolean(opts?.silent);
    setSaveBusy(true);
    setError(null);
    try {
      await api.updateCampaign(cid, sanitizeSettings(settings));
      if (opts?.reloadAfter) await reload();
      if (!silent) {
        haptic('success');
        setMessage('Настройки запуска сохранены.');
      }
    } catch (err) {
      setError((err as Error).message);
      if (!silent) haptic('warning');
      throw err;
    } finally {
      setSaveBusy(false);
    }
  };

  const openPreview = async (mode: LaunchMode) => {
    setPreviewBusy(mode);
    setSelectedMode(mode);
    setMessage(null);
    setError(null);
    try {
      await persistSettings({ silent: true, reloadAfter: false });
      const response = await api.previewCampaignLaunch(cid, mode);
      setPreview(response);
      haptic('light');
    } catch (err) {
      setError((err as Error).message);
      setPreview(null);
      haptic('warning');
    } finally {
      setPreviewBusy(null);
    }
  };

  const execute = async () => {
    if (!selectedMode) return;
    setExecuteBusy(true);
    setError(null);
    try {
      const result = await api.executeCampaignLaunch(cid, selectedMode);
      await reload();
      setPreview(null);
      setMessage(
        selectedMode === 'join'
          ? `Создано join-задач: ${result.scheduled ?? 0}.`
          : `Создано задач посева: ${result.created ?? 0}.`,
      );
      haptic('success');
    } catch (err) {
      setError((err as Error).message);
      haptic('warning');
    } finally {
      setExecuteBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Topbar
        title="Запуск кампании"
        subtitle="режимы · preview · лимиты"
        right={campaign ? <StatusPill status={campaign.status} /> : undefined}
      />

      <section className="card p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-ink-50">Меню запуска</h3>
            <p className="mt-1 text-xs leading-5 text-ink-500">
              Любой запуск сначала сохраняет настройки, потом делает preview и только после вашего подтверждения
              создаёт задачи.
            </p>
          </div>
          <button className="btn-ghost shrink-0" onClick={() => nav(`/campaigns/${cid}`)}>
            Назад
          </button>
        </div>

        <div className="mt-4 space-y-3">
          {MODE_CARDS.map((card) => {
            const isBusy = previewBusy === card.mode;
            return (
              <button
                key={card.mode}
                className="flex w-full items-center justify-between gap-3 rounded-3xl border border-ink-700/70 bg-ink-900/80 px-4 py-4 text-left transition active:scale-[0.99]"
                onClick={() => openPreview(card.mode)}
                disabled={Boolean(previewBusy || executeBusy || saveBusy)}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-ink-700/70 bg-ink-850 text-ink-100">
                    <Icon name={card.icon} size={18} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink-50">{card.title}</div>
                    <div className="mt-1 text-xs leading-5 text-ink-500">{card.subtitle}</div>
                  </div>
                </div>
                <span className="btn-ghost px-4 py-2 text-xs font-medium text-ink-200">
                  {isBusy ? 'Проверяю…' : 'Preview'}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {preview ? (
        <section className="card p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-ink-50">{modeTitle(preview.mode)}</h3>
              <p className="mt-1 text-xs leading-5 text-ink-500">
                К созданию готово {preview.summary.readyTargets} из {preview.summary.targets} чатов.
                {' '}Задач будет создано: {preview.summary.tasks}.
              </p>
            </div>
            <div className="rounded-2xl border border-ink-700/70 bg-ink-850 px-3 py-2 text-right">
              <div className="text-xs text-ink-500">пропущено</div>
              <div className="text-sm font-semibold text-ink-100">{preview.summary.skippedTargets}</div>
            </div>
          </div>

          <div className="mt-4 max-h-[320px] space-y-2 overflow-y-auto">
            {preview.items.map((item) => (
              <LaunchPreviewRow key={`${preview.mode}-${item.targetId}`} item={item} mode={preview.mode} />
            ))}
            {preview.items.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-ink-700/70 px-4 py-5 text-sm text-ink-500">
                Подходящих чатов для этого режима не найдено.
              </div>
            ) : null}
          </div>

          <div className="mt-4 flex gap-3">
            <button
              className="btn-primary min-w-[132px] px-5 py-3"
              onClick={execute}
              disabled={executeBusy || preview.summary.readyTargets === 0}
            >
              <Icon name="play" size={16} />
              {executeBusy ? 'Создаю…' : 'Принять'}
            </button>
            <button
              className="btn-ghost px-5 py-3"
              onClick={() => {
                setPreview(null);
                setSelectedMode(null);
                haptic('light');
              }}
              disabled={executeBusy}
            >
              Отменить
            </button>
          </div>
        </section>
      ) : null}

      {message ? (
        <section className="card border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm text-emerald-200">
          {message}
        </section>
      ) : null}

      {error ? (
        <section className="card border border-rose-500/20 bg-rose-500/5 p-4 text-sm text-rose-200">
          {error}
        </section>
      ) : null}

      <section className="card p-4">
        <h3 className="text-sm font-semibold text-ink-50">Окно и темп кампании</h3>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <NumberField label="Старт часа" value={settings.scheduleStartHour} min={0} max={23} unit="ч" onChange={(value) => patchSettings('scheduleStartHour', value)} />
          <NumberField label="Стоп часа" value={settings.scheduleEndHour} min={1} max={24} unit="ч" onChange={(value) => patchSettings('scheduleEndHour', value)} />
          <NumberField label="Интервал тика" value={settings.intervalMinutes} min={1} max={1440} unit="мин" onChange={(value) => patchSettings('intervalMinutes', value)} />
          <NumberField label="Чатов/день на акк" value={settings.maxChatsPerAccountPerDay} min={1} max={100} onChange={(value) => patchSettings('maxChatsPerAccountPerDay', value)} />
          <NumberField label="Пауза между чатами" value={settings.minDelayBetweenChatsSec} min={0} max={86400} unit="сек" onChange={(value) => patchSettings('minDelayBetweenChatsSec', value)} />
          <NumberField label="Вступление от" value={settings.joinDelayMinSec} min={0} max={86400} unit="сек" onChange={(value) => patchSettings('joinDelayMinSec', value)} />
          <NumberField label="Вступление до" value={settings.joinDelayMaxSec} min={0} max={86400} unit="сек" onChange={(value) => patchSettings('joinDelayMaxSec', value)} />
        </div>

        <div className="mt-4 flex items-center justify-between gap-4 rounded-3xl border border-ink-700/70 bg-ink-900/60 px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-ink-50">Уникальный диалог в чат</div>
            <div className="mt-1 text-xs text-ink-500">Запрещает повторное использование одного и того же сценария в том же чате.</div>
          </div>
          <Toggle on={settings.uniqueDialogPerChat} onChange={(value) => patchSettings('uniqueDialogPerChat', value)} />
        </div>
      </section>

      <section className="card p-4">
        <h3 className="text-sm font-semibold text-ink-50">Интервалы внутри посева</h3>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <NumberField label="Между репликами от" value={settings.seedStepDelayMinSec} min={0} max={86400} unit="сек" onChange={(value) => patchSettings('seedStepDelayMinSec', value)} />
          <NumberField label="Между репликами до" value={settings.seedStepDelayMaxSec} min={0} max={86400} unit="сек" onChange={(value) => patchSettings('seedStepDelayMaxSec', value)} />
        </div>
      </section>

      <section className="card p-4">
        <h3 className="text-sm font-semibold text-ink-50">Тайминги автоответов</h3>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <NumberField label="Reply в чат от" value={settings.chatReplyDelayMinSec} min={0} max={86400} unit="сек" onChange={(value) => patchSettings('chatReplyDelayMinSec', value)} />
          <NumberField label="Reply в чат до" value={settings.chatReplyDelayMaxSec} min={0} max={86400} unit="сек" onChange={(value) => patchSettings('chatReplyDelayMaxSec', value)} />
          <NumberField label="Reply в ЛС от" value={settings.dmReplyDelayMinSec} min={0} max={86400} unit="сек" onChange={(value) => patchSettings('dmReplyDelayMinSec', value)} />
          <NumberField label="Reply в ЛС до" value={settings.dmReplyDelayMaxSec} min={0} max={86400} unit="сек" onChange={(value) => patchSettings('dmReplyDelayMaxSec', value)} />
          <NumberField label="Typing от" value={settings.typingMinMs} min={0} max={300000} unit="мс" onChange={(value) => patchSettings('typingMinMs', value)} />
          <NumberField label="Typing до" value={settings.typingMaxMs} min={0} max={300000} unit="мс" onChange={(value) => patchSettings('typingMaxMs', value)} />
        </div>
      </section>

      <section className="card p-4">
        <h3 className="text-sm font-semibold text-ink-50">Жёсткие лимиты</h3>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <NumberField label="Ответов юзеру в чате" value={settings.maxChatRepliesPerUser} min={1} max={50} onChange={(value) => patchSettings('maxChatRepliesPerUser', value)} />
          <NumberField label="Ответов в ЛС" value={settings.maxDmRepliesPerDialog} min={1} max={50} onChange={(value) => patchSettings('maxDmRepliesPerDialog', value)} />
          <NumberField label="Реплик на посев от" value={settings.seedMessagesMin} min={1} max={50} onChange={(value) => patchSettings('seedMessagesMin', value)} />
          <NumberField label="Реплик на посев до" value={settings.seedMessagesMax} min={1} max={50} onChange={(value) => patchSettings('seedMessagesMax', value)} />
        </div>
      </section>

      <button className="btn-primary w-full" onClick={() => persistSettings({ silent: false, reloadAfter: true })} disabled={saveBusy || Boolean(previewBusy) || executeBusy}>
        <Icon name="shield" size={16} />
        {saveBusy ? 'Сохраняю…' : 'Сохранить настройки'}
      </button>
    </div>
  );
}

function LaunchPreviewRow({
  item,
  mode,
}: {
  item: CampaignLaunchPreviewItem;
  mode: LaunchMode;
}): JSX.Element {
  return (
    <div className="rounded-3xl border border-ink-700/70 bg-ink-900/60 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-ink-50">{item.title}</div>
          <div className="mt-1 truncate text-xs text-ink-500">{item.normalizedLink}</div>
        </div>
        <span className={`rounded-full px-3 py-1 text-2xs font-semibold ${previewBadgeClass(item.status)}`}>
          {previewStatusLabel(item.status)}
        </span>
      </div>
      <div className="mt-2 text-xs leading-5 text-ink-400">{previewMeta(item, mode)}</div>
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  unit?: string;
  onChange: (value: number) => void;
}): JSX.Element {
  return (
    <label className="rounded-3xl border border-ink-700/70 bg-ink-900/60 px-4 py-3">
      <div className="text-2xs uppercase tracking-wider text-ink-500">{label}</div>
      <div className="mt-2 flex items-center gap-2">
        <input
          className="w-full bg-transparent text-base font-semibold text-ink-50 outline-none"
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={(event) => {
            const next = Number(event.target.value);
            onChange(Number.isFinite(next) ? clamp(Math.round(next), min, max) : min);
          }}
        />
        {unit ? <span className="text-xs text-ink-500">{unit}</span> : null}
      </div>
    </label>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange: (value: boolean) => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light');
        onChange(!on);
      }}
      className={`relative inline-flex h-7 w-12 shrink-0 overflow-hidden rounded-full border border-white/10 transition ${on ? 'bg-grad-primary' : 'bg-ink-700'}`}
      aria-pressed={on}
    >
      <span
        className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-soft transition-all ${
          on ? 'left-[22px]' : 'left-0.5'
        }`}
      />
    </button>
  );
}

function toSettings(campaign: Campaign): LaunchSettingsState {
  return {
    scheduleStartHour: campaign.scheduleStartHour,
    scheduleEndHour: campaign.scheduleEndHour,
    intervalMinutes: campaign.intervalMinutes,
    maxChatsPerAccountPerDay: campaign.maxChatsPerAccountPerDay,
    minDelayBetweenChatsSec: campaign.minDelayBetweenChatsSec,
    joinDelayMinSec: campaign.joinDelayMinSec,
    joinDelayMaxSec: campaign.joinDelayMaxSec,
    typingMinMs: campaign.typingMinMs,
    typingMaxMs: campaign.typingMaxMs,
    seedStepDelayMinSec: campaign.seedStepDelayMinSec,
    seedStepDelayMaxSec: campaign.seedStepDelayMaxSec,
    chatReplyDelayMinSec: campaign.chatReplyDelayMinSec,
    chatReplyDelayMaxSec: campaign.chatReplyDelayMaxSec,
    dmReplyDelayMinSec: campaign.dmReplyDelayMinSec,
    dmReplyDelayMaxSec: campaign.dmReplyDelayMaxSec,
    maxChatRepliesPerUser: campaign.maxChatRepliesPerUser,
    maxDmRepliesPerDialog: campaign.maxDmRepliesPerDialog,
    seedMessagesMin: campaign.seedMessagesMin,
    seedMessagesMax: campaign.seedMessagesMax,
    uniqueDialogPerChat: campaign.uniqueDialogPerChat,
  };
}

function sanitizeSettings(settings: LaunchSettingsState): LaunchSettingsState {
  const normalized = { ...settings };
  if (normalized.scheduleStartHour > normalized.scheduleEndHour) {
    [normalized.scheduleStartHour, normalized.scheduleEndHour] = [
      normalized.scheduleEndHour,
      normalized.scheduleStartHour,
    ];
  }
  if (normalized.joinDelayMinSec > normalized.joinDelayMaxSec) {
    [normalized.joinDelayMinSec, normalized.joinDelayMaxSec] = [
      normalized.joinDelayMaxSec,
      normalized.joinDelayMinSec,
    ];
  }
  if (normalized.typingMinMs > normalized.typingMaxMs) {
    [normalized.typingMinMs, normalized.typingMaxMs] = [normalized.typingMaxMs, normalized.typingMinMs];
  }
  if (normalized.seedStepDelayMinSec > normalized.seedStepDelayMaxSec) {
    [normalized.seedStepDelayMinSec, normalized.seedStepDelayMaxSec] = [
      normalized.seedStepDelayMaxSec,
      normalized.seedStepDelayMinSec,
    ];
  }
  if (normalized.chatReplyDelayMinSec > normalized.chatReplyDelayMaxSec) {
    [normalized.chatReplyDelayMinSec, normalized.chatReplyDelayMaxSec] = [
      normalized.chatReplyDelayMaxSec,
      normalized.chatReplyDelayMinSec,
    ];
  }
  if (normalized.dmReplyDelayMinSec > normalized.dmReplyDelayMaxSec) {
    [normalized.dmReplyDelayMinSec, normalized.dmReplyDelayMaxSec] = [
      normalized.dmReplyDelayMaxSec,
      normalized.dmReplyDelayMinSec,
    ];
  }
  if (normalized.seedMessagesMin > normalized.seedMessagesMax) {
    [normalized.seedMessagesMin, normalized.seedMessagesMax] = [
      normalized.seedMessagesMax,
      normalized.seedMessagesMin,
    ];
  }
  return normalized;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function modeTitle(mode: LaunchMode) {
  return MODE_CARDS.find((item) => item.mode === mode)?.title || mode;
}

function previewStatusLabel(status: CampaignLaunchPreviewItem['status']) {
  switch (status) {
    case 'ready':
      return 'готово';
    case 'already_joined':
      return 'уже вступили';
    case 'insufficient_accounts':
      return 'нет пары A/B';
    case 'scheduled':
      return 'уже в задаче';
    case 'not_joined':
      return 'не вступили';
    case 'already_seeded':
      return 'уже был посев';
    default:
      return status;
  }
}

function previewBadgeClass(status: CampaignLaunchPreviewItem['status']) {
  switch (status) {
    case 'ready':
      return 'bg-emerald-500/15 text-emerald-200';
    case 'already_joined':
    case 'already_seeded':
    case 'scheduled':
      return 'bg-sky-500/15 text-sky-200';
    case 'insufficient_accounts':
    case 'not_joined':
      return 'bg-rose-500/15 text-rose-200';
    default:
      return 'bg-ink-700 text-ink-100';
  }
}

function previewMeta(item: CampaignLaunchPreviewItem, mode: LaunchMode) {
  if (mode === 'join') {
    if (item.status === 'ready') {
      return `Будут назначены аккаунты: ${(item.candidateAccountIds || []).join(', ')}. Текущее покрытие: ${item.currentCoverage || 0}/2.`;
    }
    if (item.status === 'already_joined') {
      return `Чат уже покрыт аккаунтами: ${item.currentCoverage || 0}/2.`;
    }
    return `Свободных аккаунтов под ротацию сейчас нет. Резервов: ${item.reservedCount || 0}.`;
  }

  if (item.status === 'ready') {
    return `Готов к посеву. Вступивших аккаунтов: ${item.joinedCount || 0}. Предыдущих посевов: ${item.seededCount || 0}.`;
  }
  if (item.status === 'scheduled') {
    return 'По этому чату уже есть активная задача посева.';
  }
  if (item.status === 'not_joined') {
    return 'Сначала нужно успешно вступить в чат минимум двумя аккаунтами.';
  }
  if (item.status === 'insufficient_accounts') {
    return 'Нужна совместимая пара ролей: A + B, A + любой или B + любой.';
  }
  return `Исторических посевов уже было: ${item.seededCount || 0}.`;
}
